import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../constants/svgIcon';
import useLocale from '../../../hooks/useLocale';
import { TranslationLocale } from '../../../i18n';
import { Album, Space } from '../../../models/SpaceStore';
import { Profile } from '../../../models/UserStore';
import { getUiLocale } from '../../../utils/i18n';
import {
  AvatarNode,
  Skeleton,
  SvgIcon,
  TextButton,
  TextButtonSize,
  TextButtonVariant,
} from '../../Molecules';

import styles from './AboutJourneySheetPopup.module.css';

type AboutJourneySheetPopupProps = {
  profile: Profile;
  album: Album;
  playCount: number;
  albumClassroom: Space;
  startVideoThumbnailUrl: string | undefined;
  onBack: () => void;
  onPressInfo: () => void;
  onPressStart: () => void;
};

export const AboutJourneySheetPopup = ({
  profile,
  album,
  playCount,
  albumClassroom,
  startVideoThumbnailUrl,
  onBack,
  // onPressInfo,
  onPressStart,
}: AboutJourneySheetPopupProps) => {
  const { t, i18n } = useTranslation();
  const l = useLocale();
  const [isClosing, setIsClosing] = useState(false);
  const [isAlbumClassroomIconAvailable, setIsAlbumClassroomIconAvailable] =
    useState(true);
  const [containerHeight, setContainerHeight] = useState(0);

  const defaultLocale = album.i18nGameSetInfo.defaultLocale;
  const albumInfo = album.i18nGameSetInfo.resources[defaultLocale];
  // const classroomInfo = l(albumClassroom.i18nClassroomInfo);

  // const classroomName =
  //   albumClassroom.type === SpaceType.Home ? profile.name : classroomInfo?.name;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const screenHeight = useMemo(() => {
    return screen.height;
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.clientHeight);
    }
  }, [containerRef]);

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat(
      getUiLocale(i18n.language as TranslationLocale),
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      },
    ).format(date);
  };

  const handleClose = (action?: () => void) => {
    setIsClosing(true);
    setTimeout(
      () => {
        if (action) {
          action();
        } else {
          onBack();
        }
      },
      action ? 300 : 200,
    );
  };

  return (
    <div
      className={`${styles.background} ${
        isClosing ? styles.backgroundClosing : ''
      }`}
    >
      <div
        className={styles.backgroundClickArea}
        onClick={() => handleClose()}
        style={{ height: screenHeight - containerHeight }}
      />
      <div
        ref={containerRef}
        className={`${styles.container} ${
          isClosing ? styles.containerClosing : ''
        }`}
        id={'container'}
      >
        <div className={styles.headerRow}>
          <div
            className={styles.iconContainerCross}
            onClick={() => handleClose()}
          >
            <SvgIcon
              name={SVGIconType.Cross}
              width={17}
              height={17}
              color="#333"
            />
          </div>
          {t('About the Journey')}
        </div>
        <hr />
        <div className={styles.titleGame}>{albumInfo?.name}</div>

        {startVideoThumbnailUrl && (
          <div className={styles.video} onClick={onPressStart}>
            <img
              className={styles.videoThumbnail}
              src={startVideoThumbnailUrl}
            />
            {/* <div className={styles.playIconWrapper}>
              <div className={styles.DefaultPlayIcon}>
                <svg width="64" height="64" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="32" fill="rgba(0, 0, 0, 0.6)" />
                  <path d="M26 20l16 12-16 12z" fill="white" />
                </svg>
              </div>
            </div> */}
          </div>
        )}

        {/* {album.revision?.period ? (
          <div className={styles.memoryArea}>
            <div className={styles.memoryTitleRow}>
              <div className={styles.title}>{t('JOYAJOY AI Memory Boost')}</div>
              <div
                className={styles.iconContainerInfo}
                onClick={() => handleClose(onPressInfo)}
              >
                <SvgIcon
                  name={SVGIconType.GameInstruction}
                  width={20}
                  height={20}
                  color="#333"
                />
              </div>
            </div>
            <div className={styles.textMinor}>
              {t('Enhance your memory retention by up to 9 times.')}
            </div>
            <div className={styles.memoryBoostRow}>
              <div className={styles.AIContainer}>
                <SvgIcon
                  name={SVGIconType.MemoryWithAI}
                  width={23}
                  height={23}
                  color="#333"
                />
              </div>
              <div className={styles.text}>{t('AI Memory Boost')}</div>
              <div className={styles.boostDays}>
                {album.revision?.period} {t('days')}
              </div>
            </div>
          </div>
        ) : null} */}

        <div className={styles.infoContainer}>
          <div className={styles.row}>
            <div className={styles.profileIcon}>
              {albumClassroom?.icon?.publicUrls?.small &&
              isAlbumClassroomIconAvailable ? (
                <img
                  src={albumClassroom?.icon?.publicUrls?.small}
                  onError={() => setIsAlbumClassroomIconAvailable(false)}
                  className={styles.spaceIcon}
                />
              ) : profile ? (
                <AvatarNode profile={profile} size={26} />
              ) : (
                <Skeleton
                  width={'26px'}
                  height={'26px'}
                  borderRadius={'1rem'}
                />
              )}
            </div>
            <div className={styles.profileName}>
              {l(albumClassroom?.i18nClassroomInfo).name}
            </div>
            {' · '}
            <div className={styles.dateMinor}>
              {formatDate(album.createdAt)}
            </div>
            {' · '}
            <div className={styles.iconContainerEarth}>
              <SvgIcon
                name={SVGIconType.Region}
                width={20}
                height={20}
                color="#808080"
                className={styles.earth}
              />
            </div>
          </div>
          <div className={styles.rowBottom}>
            <div className={styles.textMinor}>
              {t('{count} Plays', {
                count: playCount,
              })}
            </div>
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <TextButton
            text={t(startVideoThumbnailUrl ? 'Play Video' : 'Play')}
            size={TextButtonSize.Medium}
            variant={TextButtonVariant.Cyan}
            onClick={() => handleClose(onPressStart)}
          />
        </div>
      </div>
    </div>
  );
};
